import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";

export const getLeadsList = createAsyncThunk(
    'leads/getLeadsList',
    async ({ accountId, token, search, start, length, statusCode, userId }, { rejectWithValue }) => {
    try {
        let url = `${import.meta.env.VITE_API_BASE_URL}/leads/list?accountId=${accountId}&start=${start}&length=${length}&draw=1`;

        if (search && search.length > 0) {
            url += `&search[value]=${encodeURIComponent(search)}`;
        } else {
            url += `&search[value]=`;
        }

        if (statusCode) {
            url += `&statusCode=${statusCode}`;
        }

        if (userId) {
            url += `&userId=${userId}`;
        }

        const response = await fetch(url, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                'auth-token': token
            }
        });

        if (!response.ok) {
            const errorData = await response.json();
            return rejectWithValue(errorData.message || 'Errore durante il recupero delle lead');
        }

        const data = await response.json();
        return data;
    } catch (error) {
        return rejectWithValue(error.message);
    }
});

export const getLeadDetails = createAsyncThunk(
    'leads/getLeadDetails',
    async ({ leadId, token }, { rejectWithValue }) => {
    try {
        const url = `${import.meta.env.VITE_API_BASE_URL}/leads?leadId=${leadId}`;
        const response = await fetch(url, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                'auth-token': token
            }
        });

        if (!response.ok) {
            const errorData = await response.json();
            return rejectWithValue(errorData.message || 'Errore durante il recupero dei dettagli della lead');
        }

        const data = await response.json();
        return data.data || data;
    } catch (error) {
        return rejectWithValue(error.message);
    }
});

export const createLead = createAsyncThunk(
    'leads/createLead',
    async ({ lead, token }, { rejectWithValue }) => {
    try {
        const url = `${import.meta.env.VITE_API_BASE_URL}/leads`;
        const response = await fetch(url, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'auth-token': token
            },
            body: JSON.stringify({ lead })
        });
        if (!response.ok) {
            const errorData = await response.json();
            return rejectWithValue(errorData.message || 'Errore durante la creazione della lead');
        }
        const data = await response.json();
        return data.data;
    } catch (error) {
        return rejectWithValue(error.message);
    }
});

export const updateLead = createAsyncThunk(
    'leads/updateLead',
    async ({ lead, token }, { rejectWithValue }) => {
    try {
        const url = `${import.meta.env.VITE_API_BASE_URL}/leads`;
        const response = await fetch(url, {
            method: 'PATCH',
            headers: {
                'Content-Type': 'application/json',
                'auth-token': token
            },
            body: JSON.stringify({ lead })
        });
        if (!response.ok) {
            const errorData = await response.json();
            return rejectWithValue(errorData.message || 'Errore durante l\'aggiornamento della lead');
        }
        const data = await response.json();
        return data.data;
    } catch (error) {
        return rejectWithValue(error.message);
    }
});

export const updateLeadStatus = createAsyncThunk(
    'leads/updateLeadStatus',
    async ({ leadId, statusId, statusLabel, statusCode, statusColor, token }, { rejectWithValue }) => {
    try {
        const url = `${import.meta.env.VITE_API_BASE_URL}/leads/updateStatus`;
        const response = await fetch(url, {
            method: 'PATCH',
            headers: {
                'Content-Type': 'application/json',
                'auth-token': token
            },
            body: JSON.stringify({ leadId, statusId, statusLabel, statusCode, statusColor })
        });
        if (!response.ok) {
            const errorData = await response.json();
            return rejectWithValue(errorData.message || 'Errore durante l\'aggiornamento dello stato');
        }
        const data = await response.json();
        return data.data;
    } catch (error) {
        return rejectWithValue(error.message);
    }
});

export const convertLeadToCustomer = createAsyncThunk(
    'leads/convertLeadToCustomer',
    async ({ leadId, token }, { rejectWithValue }) => {
    try {
        const url = `${import.meta.env.VITE_API_BASE_URL}/leads/convertToCustomer`;
        const response = await fetch(url, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'auth-token': token
            },
            body: JSON.stringify({ leadId })
        });
        if (!response.ok) {
            const errorData = await response.json();
            return rejectWithValue(errorData.message || 'Errore durante la conversione della lead');
        }
        const data = await response.json();
        return data.data;
    } catch (error) {
        return rejectWithValue(error.message);
    }
});

export const deleteLead = createAsyncThunk(
    'leads/deleteLead',
    async ({ leadId, token }, { rejectWithValue }) => {
    try {
        const url = `${import.meta.env.VITE_API_BASE_URL}/leads?leadId=${leadId}`;
        const response = await fetch(url, {
            method: 'DELETE',
            headers: {
                'Content-Type': 'application/json',
                'auth-token': token
            }
        });
        if (!response.ok) {
            const errorData = await response.json();
            return rejectWithValue(errorData.message || 'Errore durante l\'eliminazione della lead');
        }
        return leadId;
    } catch (error) {
        return rejectWithValue(error.message);
    }
});

const leadsSlice = createSlice({
    name: 'leads',
    initialState: {
        data: null,
        loading: false,
        error: null,
        leadDetails: null,
        loadingDetails: false,
        errorDetails: null,
        createLoading: false,
        createError: null,
        updateLoading: false,
        updateError: null,
        deleteLoading: false,
        deleteError: null,
        convertLoading: false,
        convertError: null,
    },
    reducers: {
        resetLeadDetails: (state) => {
            state.leadDetails = null;
            state.loadingDetails = false;
            state.errorDetails = null;
        }
    },
    extraReducers: (builder) => {
        builder
            // getLeadsList
            .addCase(getLeadsList.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(getLeadsList.fulfilled, (state, action) => {
                state.loading = false;
                state.data = action.payload;
            })
            .addCase(getLeadsList.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload || 'Errore durante il recupero delle lead';
            })
            // getLeadDetails
            .addCase(getLeadDetails.pending, (state) => {
                state.loadingDetails = true;
                state.errorDetails = null;
            })
            .addCase(getLeadDetails.fulfilled, (state, action) => {
                state.loadingDetails = false;
                state.leadDetails = action.payload;
            })
            .addCase(getLeadDetails.rejected, (state, action) => {
                state.loadingDetails = false;
                state.errorDetails = action.payload || 'Errore durante il recupero dei dettagli della lead';
            })
            // createLead
            .addCase(createLead.pending, (state) => {
                state.createLoading = true;
                state.createError = null;
            })
            .addCase(createLead.fulfilled, (state) => {
                state.createLoading = false;
            })
            .addCase(createLead.rejected, (state, action) => {
                state.createLoading = false;
                state.createError = action.payload;
            })
            // updateLead
            .addCase(updateLead.pending, (state) => {
                state.updateLoading = true;
                state.updateError = null;
            })
            .addCase(updateLead.fulfilled, (state) => {
                state.updateLoading = false;
            })
            .addCase(updateLead.rejected, (state, action) => {
                state.updateLoading = false;
                state.updateError = action.payload;
            })
            // updateLeadStatus
            .addCase(updateLeadStatus.pending, (state) => {
                state.updateLoading = true;
                state.updateError = null;
            })
            .addCase(updateLeadStatus.fulfilled, (state, action) => {
                state.updateLoading = false;
                state.leadDetails = action.payload;
            })
            .addCase(updateLeadStatus.rejected, (state, action) => {
                state.updateLoading = false;
                state.updateError = action.payload;
            })
            // convertLeadToCustomer
            .addCase(convertLeadToCustomer.pending, (state) => {
                state.convertLoading = true;
                state.convertError = null;
            })
            .addCase(convertLeadToCustomer.fulfilled, (state) => {
                state.convertLoading = false;
            })
            .addCase(convertLeadToCustomer.rejected, (state, action) => {
                state.convertLoading = false;
                state.convertError = action.payload;
            })
            // deleteLead
            .addCase(deleteLead.pending, (state) => {
                state.deleteLoading = true;
                state.deleteError = null;
            })
            .addCase(deleteLead.fulfilled, (state) => {
                state.deleteLoading = false;
            })
            .addCase(deleteLead.rejected, (state, action) => {
                state.deleteLoading = false;
                state.deleteError = action.payload;
            });
    }
});

export const { resetLeadDetails } = leadsSlice.actions;

export const selectLeadsData = (state) => state.leads.data;
export const selectLeadsLoading = (state) => state.leads.loading;
export const selectLeadsError = (state) => state.leads.error;
export const selectLeadDetails = (state) => state.leads.leadDetails;
export const selectLeadDetailsLoading = (state) => state.leads.loadingDetails;
export const selectLeadDetailsError = (state) => state.leads.errorDetails;
export const selectLeadCreateLoading = (state) => state.leads.createLoading;
export const selectLeadCreateError = (state) => state.leads.createError;
export const selectLeadUpdateLoading = (state) => state.leads.updateLoading;
export const selectLeadUpdateError = (state) => state.leads.updateError;
export const selectLeadDeleteLoading = (state) => state.leads.deleteLoading;
export const selectLeadDeleteError = (state) => state.leads.deleteError;
export const selectLeadConvertLoading = (state) => state.leads.convertLoading;
export const selectLeadConvertError = (state) => state.leads.convertError;

export default leadsSlice.reducer;
